import { parse } from 'qs';
import * as ReactGA from 'react-ga';
import { IntercomWrapper } from 'services/intercom';
import { readCookie, readCookiesForService, setCookie } from 'utils/Cookies';
import { initializeDatadog } from 'utils/Datadog';
import { Environment } from 'utils/Environment';
import { LocalStorage } from 'utils/LocalStorage';
import type { CookieConfig } from './CookieConfig';
import { initializeGTM } from './gtm';
import {
  ANALYTICS_COOKIE_NAME,
  addUserPropertiesToHeap,
  initializeHeap,
  setupAnalyticsId,
  trackHeapEvent,
} from './heap';
import { initializeTealium } from './tealium';
import { hasUserOptedInForAnalytics, waitForEvidon } from 'hooks/useHandleEvidonCookieConsent';
import { getSubscriberRefIdQueryParam } from 'utils/FiIntegrationUtils';

// export used to expose TRACKER_IDS to tests
export const TRACKER_IDS = {
  FD_TRACKER: {
    // FirstData
    type: 'GA',
    id: 'UA-73329301-1',
    name: 'FDTRACKER',
  },
};

export const AGENT_ID_COOKIE_NAME = 'agentId';
export const SF_PARTNER_ID_COOKIE_NAME = 'salesforcePartnerId';

// Extra trackers to send GA data to (gets setup in `initialize`)
const googleAnalyticsExtraTrackerNames: string[] = [];

export { ReactGA };

export enum LeadSource {
  WEB_FORM = 'Webform',
  VALUE_DIGITAL_OR_DIGITAL_WEB = 'Value Digital or Digital Web',
  ONLINE_CHECKOUT = 'Online checkout',
}

/* Check if Global Privacy Control is enabled before setting vendor cookies or tracking user activity
 * https://jira.corp.clover.com/browse/CGH-8905
 */
export const isGpcEnabled = () => (navigator as any).globalPrivacyControl;

/* Only initialize GA Trackers & Intercom if GPC is not enabled and user has opted in for analytics
 * Do not initialize analytics for users coming from FI Integrations flow. These users are determined by the presence
 * of subscriberRefId query param existing. If the param exists, they are coming from the FI Integrations flow.
 */
export const isAnalyticsEnabled = () => {
  const isFiMerchant = !!getSubscriberRefIdQueryParam();
  return !isFiMerchant && !isGpcEnabled() && hasUserOptedInForAnalytics();
};

/* Helper function to asynchronously initialize analytics without blocking bootstrap.
 * Required since the await keyword is not supported in Bootstrap.tsx since
 * the target option in our tsconfig.json target is out of date.
 */
export const initializeAnalytics = async (testMode = false) => {
  await waitForEvidon();
  await initialize(testMode);
};

export async function initialize(testMode = false) {
  if (isAnalyticsEnabled()) {
    // Initialize Clover's Google Tag Manager
    initializeGTM();

    // Initialize Clover's Tealium Tag Manager
    initializeTealium();

    // Initialize Heap (tracking)
    initializeHeap();
  }

  const trackers: ReactGA.Tracker[] = [{ trackingId: Environment.getGAPropertyId() }];

  // Configure and Init Datadog when env is development
  if (
    Environment.current.isCloverDevEnvironment ||
    Environment.current.isCloverProdEnvironment ||
    Environment.current.isCloverStagingEnvironment
  ) {
    initializeDatadog();
  }

  if (Environment.current.isCloverProdEnvironment) {
    trackers.push({
      trackingId: TRACKER_IDS.FD_TRACKER.id,
      gaOptions: { name: TRACKER_IDS.FD_TRACKER.name },
    });
    googleAnalyticsExtraTrackerNames.push(TRACKER_IDS.FD_TRACKER.name);
  }

  if (isAnalyticsEnabled()) {
    ReactGA.initialize(trackers, { testMode, debug: Environment.current.isDev && !testMode });
    IntercomWrapper.boot();
  }

  setupAnalyticsId()
    .then(clAnalyticsId => updateServicesWithAnalyticsId(clAnalyticsId))
    .catch(err => console.info(err));
}

export function updateServicesWithAnalyticsId(clAnalyticsId: string) {
  if (!isAnalyticsEnabled()) return;

  setCookie(ANALYTICS_COOKIE_NAME, clAnalyticsId, 365 * 2);

  IntercomWrapper.update({
    analytics_id: clAnalyticsId,
  });

  ReactGA.set({ userId: clAnalyticsId }, googleAnalyticsExtraTrackerNames);
}

export function outboundLink<T extends { label: string }>(args: T) {
  if (!isAnalyticsEnabled()) {
    // Immediately resolve the Promise if DNT is enabled
    return Promise.resolve();
  }

  return new Promise(resolve => ReactGA.outboundLink(args, resolve as any, googleAnalyticsExtraTrackerNames));
}

export function trackEvent(eventAction: string, eventLabel?: string, eventValue?: number, data: object = {}) {
  if (!isAnalyticsEnabled()) return;

  ReactGA.event(
    {
      category: 'Site',
      action: eventAction,
      label: eventLabel,
      value: eventValue,
    },
    googleAnalyticsExtraTrackerNames,
  );

  trackHeapEvent(eventAction, {
    ...data,
    eventLabel,
    eventValue,
  });

  IntercomWrapper.trackEvent(eventAction, {
    ...data,
    value: eventLabel,
    eventValue,
  });
}

export async function trackPage() {
  await waitForEvidon();
  if (!isAnalyticsEnabled()) return;

  ReactGA.set({ page: window.location.pathname }, googleAnalyticsExtraTrackerNames);
  ReactGA.pageview(window.location.pathname, googleAnalyticsExtraTrackerNames);
  IntercomWrapper.update({
    current_page_url: window.location.pathname,
  });
}

// GA, Google Analytics, Heap
export function makeMiscInfoJsonString(cookieConfig: CookieConfig[], additionalFields = {}) {
  const utmCampaign = readCookie('utm_campaign') || 'Clover_Commerce_WhereToBuy';

  const miscInfoJson = {
    utm_campaign: utmCampaign,
    HeapId: readCookie('clAnalyticsId'),
    acquiring_page_url: `${window.location.host}${window.location.pathname}`,
    leadSource: LeadSource.ONLINE_CHECKOUT,
    ...additionalFields,
    ...readCookiesForService(cookieConfig, 'miscInfo'),
  };

  return JSON.stringify(miscInfoJson, (key, value) => {
    if (value !== null) return value;
  });
}

export function addUserPropertiesToAnalytics(properties: object) {
  if (!isAnalyticsEnabled()) return;

  addUserPropertiesToHeap(properties);
  IntercomWrapper.update(properties);
}

export function processCookiesForAnalytics(cookieConfig: CookieConfig[]) {
  const INTERCOM_UPDATE_DELAY = 10000;
  const params = parse(window.location.search, { ignoreQueryPrefix: true });

  // Set cookies based on query parameters
  cookieConfig.forEach(({ cookieName, isEssential }) => {
    // Skip setting non-essential cookies if GPC is enabled
    if (!isAnalyticsEnabled() && !isEssential) return;

    const queryParamValue = params[cookieName] as string;
    if (!queryParamValue) return;

    if (AGENT_ID_COOKIE_NAME === cookieName) {
      LocalStorage.trySetItem(AGENT_ID_COOKIE_NAME, queryParamValue);
    } else {
      setCookie(cookieName, queryParamValue, 90);
    }
  });

  // Set analytics properties
  if (isAnalyticsEnabled()) {
    addUserPropertiesToHeap(readCookiesForService(cookieConfig, 'heap'));
    setTimeout(
      () => IntercomWrapper.update(readCookiesForService(cookieConfig, 'intercom')),
      INTERCOM_UPDATE_DELAY,
    );
  }
}

export function getUrlAgentIdFromLocalStorage() {
  const agentId = LocalStorage.tryGetItem(AGENT_ID_COOKIE_NAME);
  return agentId;
}
